import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtRel } from "@/lib/fmt";
import { presetRange } from "./roasUtil";
import { RoasControls } from "./RoasControls";
import { RoasKpis, RoasPace } from "./RoasKpis";
import { RoasTrend } from "./RoasTrend";
import { RoasMonths } from "./RoasMonths";
import { RoasTables } from "./RoasTables";
import { RoasClients } from "./RoasClients";
import { CampaignClassifier } from "./CampaignClassifier";

const KEY = "roas-filters-v1";
const initial = () => {
  try { const s = JSON.parse(localStorage.getItem(KEY)); if (s?.since) return s; } catch { /* ignore */ }
  const [since, until] = presetRange("6m");
  return { preset: "6m", since, until, owner: "ours", picked: [], compare: true };
};

function SyncNote({ cfg, d }) {
  const s = cfg?.sync || {};
  if (!cfg?.connected) return <div className="panel p-3 text-sm text-[var(--breach)]" data-testid="roas-not-connected">Meta access token isn't set.</div>;
  return (
    <div className="text-xs text-[var(--text-dim)] flex flex-wrap gap-x-3" data-testid="roas-sync-status">
      <span>Meta synced {cfg.last_sync ? fmtRel(cfg.last_sync) : "never"} · updates every hour</span>
      {s.state === "running" && <span className="text-[var(--ai)]">Sync running{s.full ? " (full history, a few minutes)" : ""}{"\u2026"}</span>}
      {(s.state === "failed" || s.state === "partial") && <span className="text-[var(--breach)]" title={(s.errors || []).join("\n")}>Last sync {s.state === "partial" ? "partly failed" : "failed"}: {s.error || (s.errors || [])[0]}</span>}
      {d?.include_unattributed && <span>Overall ROAS includes won leads not linked to any ad</span>}
    </div>
  );
}

export function RoasPanel() {
  const [f, setF] = useState(initial);
  const [d, setD] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [camps, setCamps] = useState([]);
  const [classify, setClassify] = useState(false);
  const loadCfg = () => api.get("/roas/config").then((r) => setCfg(r.data));
  const loadCamps = () => api.get("/roas/campaigns").then((r) => setCamps(r.data.rows));
  const load = useCallback(() => api.get("/roas/overview", { params: { since: f.since, until: f.until, owner: f.owner, compare: f.compare, campaigns: f.picked.join(",") || undefined } })
    .then((r) => setD(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Couldn't load ROAS")), [f]);
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(f)); load(); }, [f, load]);
  useEffect(() => { loadCfg(); loadCamps(); }, []);
  useEffect(() => {
    const running = cfg?.sync?.state === "running";
    const t = setInterval(() => { loadCfg(); if (running) { load(); loadCamps(); } }, running ? 8000 : 300000);
    return () => clearInterval(t);
  }, [cfg?.sync?.state, load]);
  const sync = async () => {
    try { await api.post("/roas/sync", { full: !cfg?.backfilled, days: 7 }); toast.success("Pulling latest spend from Meta"); setTimeout(loadCfg, 1500); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't start sync"); }
  };
  const saveCfg = async (body) => { const r = await api.put("/roas/config", body); setCfg(r.data); load(); };
  const monthTarget = (m, v) => saveCfg({ month_targets: { ...(cfg?.month_targets || {}), [m]: v === "" ? null : Number(v) } });
  const toggle = (id) => setF({ ...f, picked: f.picked.includes(id) ? f.picked.filter((x) => x !== id) : [...f.picked, id] });
  return (
    <div className="space-y-4" data-testid="roas-panel">
      <RoasControls f={f} setF={setF} campaigns={camps} onSync={sync} syncing={cfg?.sync?.state === "running"} onClassify={() => setClassify(true)} unclassified={d?.unclassified_campaigns} />
      <SyncNote cfg={cfg} d={d} />
      {!d ? <div className="text-xs text-[var(--text-dim)]">Loading ROAS{"\u2026"}</div> : (
        <>
          {d.unclassified_campaigns > 0 && f.owner === "ours" && (
            <button className="w-full text-left panel p-3 text-sm border-[var(--warn)]" onClick={() => setClassify(true)} data-testid="roas-unclassified-banner">
              {d.unclassified_campaigns} campaign{d.unclassified_campaigns > 1 ? "s aren't" : " isn't"} marked ours, client or test yet, so they're left out. <span className="text-[var(--ai)]">Classify now {"\u2192"}</span>
            </button>
          )}
          <RoasKpis d={d} onTarget={(v) => saveCfg({ target_roas: v })} />
          <RoasPace p={d.pace} />
          <RoasTrend d={d} key={`${d.since}-${d.until}`} />
          <RoasMonths months={d.months} avgDeal={d.totals.avg_deal} onMonthTarget={monthTarget} />
          <RoasTables d={d} picked={f.picked} onToggle={toggle} />
          <RoasClients d={d} campaigns={camps} reload={load} />
        </>
      )}
      <CampaignClassifier open={classify} onClose={() => setClassify(false)} campaigns={camps} accounts={cfg?.accounts || []} onChanged={() => { loadCamps(); load(); }} />
    </div>
  );
}
